/**
 * Move an application through the pipeline — the card-level analogue of the Jobs
 * `RequisitionStatusControls`, and the same rule: **the illegal moves have no DOM
 * node.**
 *
 * `stageTransitions(stage)` is the whole list, mapped once. There is no loop over the
 * seven stages computing a `disabled` flag, because a disabled attribute is a
 * rendering decision an operator removes in devtools, and a greyed control asserts
 * "the system will not let you" about a move that exists for nobody. `hired`,
 * `rejected` and `withdrawn` are terminal — their allowed set is `[]` — so a finished
 * application renders no move buttons and one sentence.
 *
 * ── The one deliberate exception: a disabled Hire ────────────────────────────
 *
 * `hired` is not in any allowed set (the `/transition` endpoint refuses it), so it is
 * never a move button. But an application at `offer` *can* be hired — through the
 * Phase 2 hire action, not this screen — so at `offer` a single **disabled** "Hire"
 * control is shown with a tooltip pointing at that action. It is the one disabled
 * affordance here, and it is honest: it says "not from here", not "not by you".
 *
 * ── The refusal is the authority ─────────────────────────────────────────────
 *
 * A stale card can still issue a move the server refuses `409
 * WORKFORCE_INVALID_STATE_TRANSITION`, and that refusal is rendered — the server's own
 * sentence through `ApiErrorNote`, and the server's own `allowed_transitions[]` printed
 * beside it rather than re-derived from this client's copy.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { WorkforceApiError } from '../../api/errors';
import { allowedTransitionsFromRefusal } from '../../api/useApiMutation';
import {
  isHireableStage,
  isTerminalStage,
  stageTransitions,
  STAGE_MOVE_LABEL,
  type ApplicationStage,
} from '../../recruiting/application';
import { ApiErrorNote } from '../../states/ApiErrorNote';

export interface ApplicationStageControlsProps {
  readonly appId: string;
  readonly stage: ApplicationStage;
  readonly pending: boolean;
  /** The refusal of *this card's* last move, or `null`. Shared mutation, keyed by card. */
  readonly error: WorkforceApiError | null;
  readonly onMove: (to: ApplicationStage) => void;
}

export function ApplicationStageControls({
  appId,
  stage,
  pending,
  error,
  onMove,
}: ApplicationStageControlsProps): ReactNode {
  const { t } = useI18n();
  const moves = stageTransitions(stage);
  const serverAllowed = error === null ? [] : allowedTransitionsFromRefusal(error);

  return (
    <div className="wp-board__moves" data-testid={`w3-moves-${appId}`}>
      {isTerminalStage(stage) ? (
        <p className="wp-small wp-muted" data-testid={`w3-terminal-${appId}`}>
          {t('recruiting.pipeline.transition.terminal')}
        </p>
      ) : (
        <>
          <div className="wp-toggle-group">
            {moves.map((move) => (
              <button
                key={move}
                className="wp-button"
                type="button"
                data-testid={`w3-move-${appId}-${move}`}
                disabled={pending}
                onClick={() => {
                  onMove(move);
                }}
              >
                {t(STAGE_MOVE_LABEL[move])}
              </button>
            ))}
            {isHireableStage(stage) ? (
              // The one disabled affordance: hiring is a separate Phase 2 action, and
              // `/transition` refuses `→ hired`. The tooltip says where the real one is.
              <button
                className="wp-button"
                type="button"
                data-testid={`w3-hire-${appId}`}
                disabled
                title={t('recruiting.pipeline.hire.tooltip')}
                aria-describedby={`w3-hire-hint-${appId}`}
              >
                {t('recruiting.pipeline.hire.label')}
              </button>
            ) : null}
          </div>
          {isHireableStage(stage) ? (
            <p className="wp-small wp-muted" id={`w3-hire-hint-${appId}`}>
              {t('recruiting.pipeline.hire.tooltip')}
            </p>
          ) : null}
        </>
      )}

      {error === null ? null : (
        <>
          <ApiErrorNote
            error={error}
            subject={t('recruiting.pipeline.subjectWord')}
            testId={`w3-move-error-${appId}`}
          />
          {serverAllowed.length === 0 ? null : (
            <p className="wp-small" data-testid={`w3-move-allowed-${appId}`}>
              {t('recruiting.pipeline.transition.serverAllowed', {
                list: serverAllowed.join(', '),
              })}
            </p>
          )}
        </>
      )}
    </div>
  );
}
